import React from "react";

export const PAYROLL_GROUP_ROW_HEIGHT = 32;

const GROUP_GRADIENTS: Record<
  "percepciones" | "deducciones" | "otros" | "final",
  string
> = {
  percepciones: "linear-gradient(90deg, #50B7C0 10%, #36cfc9 90%)",
  deducciones: "linear-gradient(90deg, #f43f5e 0%, #e11d48 100%)",
  otros: "linear-gradient(90deg, #0ea5e9 0%, #0891b2 100%)",
  final: "linear-gradient(90deg, #eab308 0%, #f59e0b 100%)",
};

export function payrollGroupHeaderStyle(
  variant: "percepciones" | "deducciones" | "otros" | "final"
): React.CSSProperties {
  return {
    background: GROUP_GRADIENTS[variant],
    color: "white",
    border: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
    padding: "0.6rem 0.5rem",
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.05em",
  };
}

export function payrollSubHeaderStyle(
  overrides: React.CSSProperties = {}
): React.CSSProperties {
  return {
    background: "#f8fafc",
    borderLeft: "none",
    borderRight: "none",
    borderTop: "none",
    borderBottom: "2px solid #e2e8f0",
    position: "sticky",
    top: `${PAYROLL_GROUP_ROW_HEIGHT}px`,
    zIndex: 9,
    padding: "0.4rem 0.3rem",
    fontSize: "0.65rem",
    fontWeight: 600,
    color: "#475569",
    whiteSpace: "nowrap",
    transition: "background 0.2s ease",
    lineHeight: 1.2,
    ...overrides,
  };
}

const HEADER_LINE_BREAKS: Record<string, string[]> = {
  "Sueldo base 15": ["Sueldo", "base 15"],
  "Salario diario": ["Salario", "diario"],
  "Pago por hora": ["Pago", "por hora"],
  "Sueldo x hora": ["Sueldo", "x hora"],
  "Días laborados": ["Días", "laborados"],
  "Jornadas completadas": ["Jornadas", "completadas"],
  "Jornadas compl.": ["Jornadas", "compl."],
  "Horas trabajadas": ["Horas", "trabajadas"],
  "$ Horas trab.": ["$ Horas", "trab."],
  "Horas normales": ["Horas", "normales"],
  "Turnos dobles": ["Turnos", "dobles"],
  "$ T. dobles": ["$ T.", "dobles"],
  "Horas extras": ["Horas", "extras"],
  "$ H. extras": ["$ H.", "extras"],
  "Descanso trabajado": ["Descanso", "trabajado"],
  "Descansos trab.": ["Descansos", "trab."],
  "$ Desc. trab.": ["$ Desc.", "trab."],
  "Descanso disfrutado": ["Descanso", "disfrutado"],
  "Días festivos": ["Días", "festivos"],
  "Días vacaciones": ["Días", "vacaciones"],
  "$ Vacaciones": ["$", "Vacaciones"],
  "Días incapacidad": ["Días", "incapacidad"],
  "$ Incapacidad": ["$", "Incapacidad"],
  "$ Jornadas": ["$", "Jornadas"],
  "$ Festivo": ["$", "Festivo"],
  Tarjetas: ["Tarjetas"],
  "Pago con tarjeta": ["Pago con", "tarjeta"],
  "Efectivo a pagar": ["Efectivo", "a pagar"],
  "Total de deducciones": ["Total", "deducciones"],
  "Total descuentos": ["Total", "descuentos"],
  "Total ingresos": ["Total", "ingresos"],
  "Total a pagar": ["Total", "a pagar"],
  "Pago estimado": ["Pago", "estimado"],
  "Prima vacacional": ["Prima", "vacacional"],
};

export function getPayrollHeaderLines(text: string): string[] {
  return HEADER_LINE_BREAKS[text] || [text];
}

export function PayrollSubHeaderCell({
  label,
  onClick,
  active,
  sortOrder,
}: {
  label: string;
  onClick?: () => void;
  active?: boolean;
  sortOrder?: "asc" | "desc";
}) {
  const lines = getPayrollHeaderLines(label);

  return (
    <th
      className="align-middle"
      style={payrollSubHeaderStyle(onClick ? { cursor: "pointer" } : {})}
      onClick={onClick}
      onMouseEnter={
        onClick
          ? (e) => {
              e.currentTarget.style.background = "#e2e8f0";
            }
          : undefined
      }
      onMouseLeave={
        onClick
          ? (e) => {
              e.currentTarget.style.background = "#f8fafc";
            }
          : undefined
      }
    >
      <div className="d-flex flex-column align-items-center justify-content-center">
        {lines.map((line, i) => (
          <span key={i}>{line}</span>
        ))}
        {active && sortOrder && (
          <i
            className={`fa-solid fa-sort-${sortOrder === "asc" ? "up" : "down"} mt-1`}
            style={{ fontSize: "0.55rem" }}
          />
        )}
      </div>
    </th>
  );
}

const PayrollHeader = ({
  setSort,
  sort,
}: {
  setSort: (column: string, order: string) => void;
  sort: { column: string; order: string };
}) => {
  return (
    <thead className="text-center">
      <tr className="text-uppercase">
        <th
          colSpan={14}
          className="align-middle"
          style={payrollGroupHeaderStyle("percepciones")}
        >
          Percepciones
        </th>
        <th
          colSpan={2}
          className="align-middle"
          style={payrollGroupHeaderStyle("deducciones")}
        >
          Deducciones
        </th>
        <th
          colSpan={1}
          className="align-middle"
          style={payrollGroupHeaderStyle("otros")}
        >
          Otros
        </th>
        <th
          colSpan={2}
          className="align-middle"
          style={payrollGroupHeaderStyle("final")}
        >
          Final
        </th>
      </tr>

      <tr>
        {titles.map((title) => (
          <PayrollSubHeaderCell
            key={title}
            label={title}
            onClick={() =>
              setSort(title, sort.order === "asc" ? "desc" : "asc")
            }
            active={title === sort.column}
            sortOrder={sort.order as "asc" | "desc"}
          />
        ))}
      </tr>
    </thead>
  );
};

/** Mismas columnas que la tabla del cuadro de mando (export Excel, etc.). */
export const PAYROLL_CUADRO_COLUMN_LABELS: string[] = [
  "Chilito",
  "Sueldo base 15",
  "Salario diario",
  "Pago por hora",
  "Días laborados",
  "Jornadas completadas",
  "Horas trabajadas",
  "Horas normales",
  "Turnos dobles",
  "Horas extras",
  "Descanso trabajado",
  "Descanso disfrutado",
  "Días festivos",
  "Bonos",
  "Total de deducciones",
  "Tarjetas",
  "Incidencias",
  "Detalles",
  "Pago estimado",
];

export default PayrollHeader;

const titles = PAYROLL_CUADRO_COLUMN_LABELS;
